import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { buildRegionLoginUrl } from '../../utils/regionRedirect';

const REDIRECT_DELAY_MS = 4000;

interface RegionRedirectNoticeProps {
    redirectTo: string;
    region: string;
    email?: string;
}

// Shown when the login was refused because the account belongs to another
// regional stack: explains the move and sends the browser there.
export default function RegionRedirectNotice({
    redirectTo,
    region,
    email,
}: RegionRedirectNoticeProps) {
    const { t } = useTranslation();
    const targetUrl = buildRegionLoginUrl(redirectTo, email);

    useEffect(() => {
        const timer = window.setTimeout(
            () => window.location.assign(targetUrl),
            REDIRECT_DELAY_MS
        );
        return () => window.clearTimeout(timer);
    }, [targetUrl]);

    return (
        <Alert className="mb-4">
            <AlertTitle>
                {t('login.regionRedirect.title', {
                    region: region.toUpperCase(),
                })}
            </AlertTitle>
            <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span>
                    {t('login.regionRedirect.message', {
                        host: new URL(targetUrl).host,
                    })}
                </span>
                <Button
                    type="button"
                    size="sm"
                    onClick={() => window.location.assign(targetUrl)}
                >
                    {t('login.regionRedirect.button')}
                </Button>
            </AlertDescription>
        </Alert>
    );
}
